import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import moment from "moment";
import axios from "axios";
import { API_URL } from "../config.js";
import { showToast } from "./toast.jsx";
import {
  Users,
  IndianRupee,
  TrendingUp,
  Percent,
  CheckCircle,
  Clock,
  Award,
  CreditCard,
  ShoppingBag,
  Layers,
  ArrowUpRight,
  AlertCircle
} from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Dashboard.css";
import "./DistributorDashboard.css";

Chart.register(...registerables);

const DistributorDashboard = () => {
  const navigate = useNavigate();
  const [statsData, setStatsData] = useState(null);
  const [monthlyTrends, setMonthlyTrends] = useState([]);
  const [servicesBreakdown, setServicesBreakdown] = useState([]);
  const [statusBreakdown, setStatusBreakdown] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [commTotal, setCommTotal] = useState(0);
  const [commPage, setCommPage] = useState(1);
  const [commLimit] = useState(10);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [activeTab, setActiveTab] = useState("commissions");
  const [loading, setLoading] = useState(true);



  // Date Range state
  const [dateRange, setDateRange] = useState({
    startDate: moment().subtract(29, "days"),
    endDate: moment(),
  });
  const { startDate, endDate } = dateRange;

  const handleDateApply = (event, picker) => {
    setDateRange({
      startDate: picker.startDate,
      endDate: picker.endDate,
    });
  };

  const ranges = {
    Today: [moment(), moment()],
    Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
    "Last 7 Days": [moment().subtract(6, "days"), moment()],
    "Last 30 Days": [moment().subtract(29, "days"), moment()],
    "This Month": [moment().startOf("month"), moment().endOf("month")],
    "Last Month": [
      moment().subtract(1, "month").startOf("month"),
      moment().subtract(1, "month").endOf("month"),
    ],
  };

  const formatDate = (date) => {
    if (!date) return "";
    return date.format("YYYY-MM-DD");
  };

  const getQueryParams = () => {
    if (startDate && endDate) {
      return `?startDate=${formatDate(startDate)}&endDate=${formatDate(endDate)}`;
    }
    return "";
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const query = getQueryParams();
      const response = await axios.get(`${API_URL}distributor-dashboard/stats${query}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
      });
      if (response.data && response.data.success) {
        setStatsData(response.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch distributor stats", err);
    }
  };

  const fetchMonthlyTrends = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_URL}distributor-dashboard/monthly-trend`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
      });
      if (response.data && response.data.success) {
        setMonthlyTrends(response.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch monthly trends", err);
    }
  };

  const fetchServicesBreakdown = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_URL}distributor-dashboard/services-breakdown`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
      });
      if (response.data && response.data.success) {
        setServicesBreakdown(response.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch services breakdown", err);
    }
  };

  const fetchStatusBreakdown = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_URL}distributor-dashboard/status-breakdown`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
      });
      if (response.data && response.data.success) {
        setStatusBreakdown(response.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch status breakdown", err);
    }
  };

  const fetchCommissions = async () => {
    try {
      const token = localStorage.getItem("authToken");
      let url = `${API_URL}distributor-dashboard/commissions?page=${commPage}&limit=${commLimit}`;
      if (selectedStatus) {
        url += `&status=${selectedStatus}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
      });
      if (response.data && response.data.success) {
        setCommissions(response.data.data.list || []);
        setCommTotal(response.data.data.total || 0);
      }
    } catch (err) {
      console.error("Failed to fetch commissions", err);
    }
  };

  const fetchPayouts = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_URL}distributor-dashboard/payouts`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
      });
      if (response.data && response.data.success) {
        setPayouts(response.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch payouts", err);
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchStats(),
        fetchMonthlyTrends(),
        fetchServicesBreakdown(),
        fetchStatusBreakdown(),
        fetchCommissions(),
        fetchPayouts(),
      ]);
    } catch (err) {
      console.error("Error loading distributor dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
  }, [startDate, endDate]);

  useEffect(() => {
    fetchCommissions();
  }, [commPage, selectedStatus]);

  const distributorInfo = statsData?.distributor || {};
  const stats = statsData?.stats || {};
  const benchmarks = statsData?.benchmarks || {};

  // Chart data for monthly revenue and commission
  const trendLabels = monthlyTrends.map((t) => t.label);
  const trendBusinessData = monthlyTrends.map((t) => t.business);
  const trendCommissionData = monthlyTrends.map((t) => t.commission);

  const monthlyChartData = {
    labels: trendLabels.length > 0 ? trendLabels : ["Month 1", "Month 2", "Month 3", "Month 4", "Month 5", "Month 6"],
    datasets: [
      {
        type: "bar",
        label: "Gross Business (₹)",
        data: trendBusinessData.length > 0 ? trendBusinessData : [0, 0, 0, 0, 0, 0],
        backgroundColor: "rgba(52, 143, 226, 0.75)",
        borderColor: "#348fe2",
        borderWidth: 1,
        borderRadius: 6,
        yAxisID: "y",
      },
      {
        type: "line",
        label: "Commission Earned (₹)",
        data: trendCommissionData.length > 0 ? trendCommissionData : [0, 0, 0, 0, 0, 0],
        borderColor: "#10b981",
        backgroundColor: "rgba(16, 185, 129, 0.2)",
        borderWidth: 3,
        pointBackgroundColor: "#ffffff",
        pointBorderColor: "#10b981",
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
        fill: false,
        tension: 0.35,
        yAxisID: "y1",
      },
    ],
  };

  const formatShortRupee = (value) => {
    if (value === 0 || value === '0') return '₹0';
    const val = Number(value);
    if (isNaN(val)) return '₹0';
    const absVal = Math.abs(val);
    const sign = val < 0 ? '-' : '';
    if (absVal >= 10000000) {
      const cr = val / 10000000;
      return `${sign}₹${cr % 1 === 0 ? cr : cr.toFixed(2).replace(/\.00$/, '')} Cr`;
    } else if (absVal >= 100000) {
      const lk = val / 100000;
      return `${sign}₹${lk % 1 === 0 ? lk : lk.toFixed(1).replace(/\.0$/, '')} L`;
    } else if (absVal >= 1000) {
      const k = val / 1000;
      return `${sign}₹${k % 1 === 0 ? k : k.toFixed(1).replace(/\.0$/, '')} K`;
    }
    return `₹${val.toLocaleString('en-IN')}`;
  };

  const monthlyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    scales: {
      y: {
        type: "linear",
        display: true,
        position: "left",
        beginAtZero: true,
        title: {
          display: true,
          text: "Gross Business",
          color: "#64748b",
          font: { size: 12 },
        },
        grid: { color: "rgba(0,0,0,0.04)" },
        ticks: {
          callback: (value) => formatShortRupee(value),
        },
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        beginAtZero: true,
        title: {
          display: true,
          text: "Commission",
          color: "#10b981",
          font: { size: 12 },
        },
        grid: { drawOnChartArea: false },
        ticks: {
          callback: (value) => formatShortRupee(value),
        },
      },
      x: {
        grid: { display: false },
      },
    },
    plugins: {
      legend: {
        position: "top",
        labels: { font: { weight: "600" } },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ₹${Number(context.raw || 0).toLocaleString("en-IN")}`;
          },
        },
      },
    },
  };

  const formatRupee = (val) => {
    return `₹${Number(val || 0).toLocaleString("en-IN")}`;
  };

  const benchmarkRows = [
    {
      metric: "New Referred Leads",
      icon: <Users size={18} className="text-primary" />,
      today: benchmarks?.referrals?.today ?? 0,
      mtd: benchmarks?.referrals?.mtd ?? 0,
      target: benchmarks?.referrals?.target ?? 50,
      status: benchmarks?.referrals?.status ?? "On Track",
    },
    {
      metric: "Paid Conversions",
      icon: <ShoppingBag size={18} className="text-success" />,
      today: benchmarks?.conversions?.today ?? 0,
      mtd: benchmarks?.conversions?.mtd ?? 0,
      target: benchmarks?.conversions?.target ?? 10,
      status: benchmarks?.conversions?.status ?? "On Track",
    },
    {
      metric: "Gross Business Volume",
      icon: <IndianRupee size={18} className="text-info" />,
      today: benchmarks?.business?.today ?? "₹0",
      mtd: benchmarks?.business?.mtd ?? "₹0",
      target: benchmarks?.business?.target ?? "₹2,00,000",
      status: benchmarks?.business?.status ?? "On Track",
    },
    {
      metric: "Commission Accrued",
      icon: <TrendingUp size={18} className="text-warning" />,
      today: benchmarks?.commission?.today ?? "₹0",
      mtd: benchmarks?.commission?.mtd ?? "₹0",
      target: benchmarks?.commission?.target ?? "₹30,000",
      status: benchmarks?.commission?.status ?? "On Track",
    },
    {
      metric: "Settled Payouts",
      icon: <CreditCard size={18} className="text-teal" />,
      today: benchmarks?.payouts?.today ?? "₹0",
      mtd: benchmarks?.payouts?.mtd ?? "₹0",
      target: benchmarks?.payouts?.target ?? "—",
      status: benchmarks?.payouts?.status ?? "On Track",
    },
  ];

  return (
    <div className="page-header-fixed page-sidebar-fixed fade in mt-5">
      {/* Header & Date Picker Filter */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div>
          <h1 className="page-header mb-1">
            Distributor <small>Dashboard</small>
          </h1>
          <span className="text-muted small">
            Real-time referral performance, revenue attribution, and commission analytics
          </span>
        </div>

        <DateRangePicker
          initialSettings={{
            ranges: ranges,
            startDate: startDate,
            endDate: endDate,
            locale: { format: "DD-MM-YYYY" },
          }}
          onApply={handleDateApply}
        >
          <div className="input-group" style={{ cursor: "pointer", width: "260px" }}>
            <input
              type="text"
              className="form-control"
              value={
                startDate && endDate
                  ? `${startDate.format("DD-MM-YYYY")} - ${endDate.format("DD-MM-YYYY")}`
                  : "Select Date Range"
              }
              readOnly
              style={{ background: "#fff", cursor: "pointer", fontWeight: "500" }}
            />
            <span className="input-group-text bg-light">
              <FontAwesomeIcon icon={faCalendarAlt} />
            </span>
          </div>
        </DateRangePicker>
      </div>

      {/* 6 Metric Count Widgets matching CRM Dashboard Design */}
      <div className="row mb-4 g-3">
        <div className="col-xl-4 col-md-6">
          <div className="widget widget-stats bg-gradient-blue-dark inverse-mode" style={{ minHeight: '78px', padding: '10px 16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', borderRadius: '6px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}>
            <div style={{ textAlign: 'left', width: '100%' }}>
              <span style={{ fontSize: '14.5px', fontWeight: '600', color: '#ffffff', letterSpacing: '0.2px', lineHeight: '1.2' }}>Total Referrals</span>
            </div>
            <div style={{ textAlign: 'right', width: '100%', marginTop: '4px' }}>
              <span style={{ fontSize: '26px', fontWeight: '700', color: '#ffffff', lineHeight: '1.1' }}>{Number(stats.totalReferrals || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="col-xl-4 col-md-6">
          <div className="widget widget-stats bg-gradient-teal inverse-mode" style={{ minHeight: '78px', padding: '10px 16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', borderRadius: '6px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}>
            <div style={{ textAlign: 'left', width: '100%' }}>
              <span style={{ fontSize: '14.5px', fontWeight: '600', color: '#ffffff', letterSpacing: '0.2px', lineHeight: '1.2' }}>Active Subscriptions</span>
            </div>
            <div style={{ textAlign: 'right', width: '100%', marginTop: '4px' }}>
              <span style={{ fontSize: '26px', fontWeight: '700', color: '#ffffff', lineHeight: '1.1' }}>{Number(stats.activeSubscriptions || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className="col-xl-4 col-md-6">
          <div className="widget widget-stats bg-gradient-purple inverse-mode" style={{ minHeight: '78px', padding: '10px 16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', borderRadius: '6px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}>
            <div style={{ textAlign: 'left', width: '100%' }}>
              <span style={{ fontSize: '14.5px', fontWeight: '600', color: '#ffffff', letterSpacing: '0.2px', lineHeight: '1.2' }}>Gross Business Generated</span>
            </div>
            <div style={{ textAlign: 'right', width: '100%', marginTop: '4px' }}>
              <span style={{ fontSize: '26px', fontWeight: '700', color: '#ffffff', lineHeight: '1.1' }}>{formatRupee(stats.totalBusiness)}</span>
            </div>
          </div>
        </div>

        <div className="col-xl-4 col-md-6">
          <div className="widget widget-stats bg-gradient-green inverse-mode" style={{ minHeight: '78px', padding: '10px 16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', borderRadius: '6px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}>
            <div style={{ textAlign: 'left', width: '100%' }}>
              <span style={{ fontSize: '14.5px', fontWeight: '600', color: '#ffffff', letterSpacing: '0.2px', lineHeight: '1.2' }}>Total Commission Earned</span>
            </div>
            <div style={{ textAlign: 'right', width: '100%', marginTop: '4px' }}>
              <span style={{ fontSize: '26px', fontWeight: '700', color: '#ffffff', lineHeight: '1.1' }}>{formatRupee(stats.totalCommission)}</span>
            </div>
          </div>
        </div>

        <div className="col-xl-4 col-md-6">
          <div className="widget widget-stats bg-gradient-indigo inverse-mode" style={{ minHeight: '78px', padding: '10px 16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', borderRadius: '6px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}>
            <div style={{ textAlign: 'left', width: '100%' }}>
              <span style={{ fontSize: '14.5px', fontWeight: '600', color: '#ffffff', letterSpacing: '0.2px', lineHeight: '1.2' }}>Conversion Rate</span>
            </div>
            <div style={{ textAlign: 'right', width: '100%', marginTop: '4px' }}>
              <span style={{ fontSize: '26px', fontWeight: '700', color: '#ffffff', lineHeight: '1.1' }}>{stats.conversionRate || 0}%</span>
            </div>
          </div>
        </div>

        <div className="col-xl-4 col-md-6">
          <div className="widget widget-stats bg-gradient-red inverse-mode" style={{ minHeight: '78px', padding: '10px 16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', borderRadius: '6px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}>
            <div style={{ textAlign: 'left', width: '100%' }}>
              <span style={{ fontSize: '14.5px', fontWeight: '600', color: '#ffffff', letterSpacing: '0.2px', lineHeight: '1.2' }}>Average Order Value</span>
            </div>
            <div style={{ textAlign: 'right', width: '100%', marginTop: '4px' }}>
              <span style={{ fontSize: '26px', fontWeight: '700', color: '#ffffff', lineHeight: '1.1' }}>{formatRupee(stats.avgOrderValue)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 1. Full-Width Row: Performance Benchmarks */}
      <div className="row mb-4">
        <div className="col-lg-12">
          <div className="panel panel-default with-rounded-corner with-shadow mb-0">
            <div className="panel-heading">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="p-2 rounded-4"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(52, 143, 226, 0.1) 0%, rgba(52, 143, 226, 0.2) 100%)",
                  }}
                >
                  <TrendingUp className="text-primary" size={20} />
                </div>
                <div>
                  <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: "1.1rem" }}>
                    Performance Benchmarks
                  </h4>
                  <span className="text-muted small">Daily vs Monthly Target Tracking</span>
                </div>
              </div>
            </div>
            <div className="panel-body p-4">
              <div className="summary-table">
                <div className="summary-header">
                  <div className="summary-col-metric">Metric</div>
                  <div className="summary-col-today">Today</div>
                  <div className="summary-col-mtd">MTD</div>
                  <div className="summary-col-target">Target</div>
                  <div className="summary-col-status">Status</div>
                </div>

                <div className="d-flex flex-column">
                  {benchmarkRows.map((row, idx) => (
                    <div key={idx} className="summary-row">
                      <div className="summary-col-metric">
                        <div className="summary-icon-wrapper">{row.icon}</div>
                        <span className="summary-label">{row.metric}</span>
                      </div>
                      <div className="summary-col-today fw-bold text-dark">{row.today}</div>
                      <div className="summary-col-mtd text-muted small">{row.mtd}</div>
                      <div className="summary-col-target fw-bold text-dark">{row.target}</div>
                      <div className="summary-col-status">
                        <div className="d-flex align-items-center gap-2">
                          {row.status === "On Track" ? (
                            <CheckCircle size={16} className="text-success" />
                          ) : (
                            <AlertCircle size={16} className="text-warning" />
                          )}
                          <span
                            className="text-xs text-uppercase fw-bold"
                            style={{
                              color: row.status === "On Track" ? "#10b981" : "#f59e0b",
                              fontSize: "11px",
                            }}
                          >
                            {row.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Full-Width Row: Business & Commission Growth Trend */}
      <div className="row mb-4">
        <div className="col-lg-12">
          <div className="panel panel-default with-rounded-corner with-shadow mb-0">
            <div className="panel-heading">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="p-2 rounded-4"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(52, 143, 226, 0.1) 0%, rgba(52, 143, 226, 0.2) 100%)",
                  }}
                >
                  <TrendingUp className="text-primary" size={20} />
                </div>
                <div>
                  <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: "1.1rem" }}>
                    Business & Commission Growth Trend
                  </h4>
                  <span className="text-muted small">Monthly gross referral volume vs commission earned</span>
                </div>
              </div>
            </div>
            <div className="panel-body p-4" style={{ height: "340px" }}>
              <Bar data={monthlyChartData} options={monthlyChartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* 3. Combined Row: Commission Status Breakdown & Top Services Subscribed */}
      <div className="row mb-5 g-4">
        {/* Commission Status Donut Chart */}
        <div className="col-lg-6">
          <div className="panel panel-default with-rounded-corner with-shadow h-100 mb-0">
            <div className="panel-heading">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="p-2 rounded-4"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.2) 100%)",
                  }}
                >
                  <Award className="text-success" size={20} />
                </div>
                <div>
                  <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: "1.1rem" }}>
                    Commission Status Breakdown
                  </h4>
                  <span className="text-muted small">Distribution of payout statuses</span>
                </div>
              </div>
            </div>
            <div className="panel-body p-4">
              <div className="row align-items-center">
                <div className="col-sm-6" style={{ height: "230px" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusBreakdown.length > 0 ? statusBreakdown : [{ status: "No Data", count: 1, amount: 0, color: "#cbd5e1" }]}
                        dataKey="count"
                        nameKey="status"
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={80}
                        paddingAngle={4}
                      >
                        {statusBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="custom-tooltip-glass">
                                <p className="mb-1 fw-bold" style={{ color: data.color }}>
                                  {data.status}
                                </p>
                                <p className="mb-0 text-dark fw-bolder">
                                  {data.count} Orders (₹{Number(data.amount || 0).toLocaleString()})
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="col-sm-6">
                  <div className="d-flex flex-column gap-2">
                    {statusBreakdown.map((item) => (
                      <div
                        key={item.status}
                        className="d-flex align-items-center justify-content-between p-2 rounded-2 bg-light bg-opacity-50"
                      >
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle"
                            style={{ backgroundColor: item.color, width: "10px", height: "10px" }}
                          />
                          <span className="small fw-semibold text-dark">{item.status}</span>
                        </div>
                        <div className="text-end">
                          <span className="d-block small fw-bold text-dark">₹{Number(item.amount || 0).toLocaleString()}</span>
                          <span className="text-muted" style={{ fontSize: "0.7rem" }}>{item.count} orders</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Services Breakdown */}
        <div className="col-lg-6">
          <div className="panel panel-default with-rounded-corner with-shadow mb-0 h-100">
            <div className="panel-heading">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="p-2 rounded-4"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.2) 100%)",
                  }}
                >
                  <Layers className="text-warning" size={20} />
                </div>
                <div>
                  <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: "1.1rem" }}>
                    Top Services Subscribed
                  </h4>
                  <span className="text-muted small">By referred clients</span>
                </div>
              </div>
            </div>
            <div className="panel-body p-4">
              {servicesBreakdown.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <ShoppingBag className="mx-auto mb-2 opacity-50" size={32} />
                  <p className="mb-0 small">No subscription data recorded yet.</p>
                </div>
              ) : (
                servicesBreakdown.slice(0, 5).map((srv, idx) => {
                  const maxVol = servicesBreakdown[0]?.volume || 1;
                  const pct = Math.min(100, Math.round((srv.volume / maxVol) * 100));
                  return (
                    <div key={idx} className="service-meter-row">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="fw-semibold text-dark small text-truncate" style={{ maxWidth: "200px" }}>
                          {srv.serviceName}
                        </span>
                        <span className="text-muted small">
                          <strong>{srv.count}</strong> subs • <strong className="text-dark">₹{srv.volume.toLocaleString()}</strong>
                        </span>
                      </div>
                      <div className="progress" style={{ height: "6px", backgroundColor: "#f1f5f9" }}>
                        <div
                          className="progress-bar bg-primary"
                          role="progressbar"
                          style={{ width: `${pct}%`, borderRadius: "4px" }}
                          aria-valuenow={pct}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        />
                      </div>
                      <div className="d-flex justify-content-between mt-1 text-xs text-muted" style={{ fontSize: "0.72rem" }}>
                        <span>Earned: ₹{srv.commission.toLocaleString()}</span>
                        <span>{pct}% share</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabbed Data Tables: Recent Commissions & Payout History */}
      <div className="panel panel-default with-rounded-corner with-shadow mb-5">
        <div className="panel-heading pb-0">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div className="d-flex align-items-center gap-3">
              <div
                className="p-2 rounded-4"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(52, 143, 226, 0.1) 0%, rgba(52, 143, 226, 0.2) 100%)",
                }}
              >
                <CreditCard className="text-primary" size={20} />
              </div>
              <div>
                <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: "1.1rem" }}>
                  Commission & Settlement Ledger
                </h4>
                <span className="text-muted small">Detailed record of referred client orders and commission payouts</span>
              </div>
            </div>

            {/* Status Filter for Commissions */}
            {activeTab === "commissions" && (
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted small fw-semibold">Filter:</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "140px" }}
                  value={selectedStatus}
                  onChange={(e) => {
                    setSelectedStatus(e.target.value);
                    setCommPage(1);
                  }}
                >
                  <option value="">All Statuses</option>
                  <option value="Paid">Paid</option>
                  <option value="Approved">Approved</option>
                  <option value="Pending">Pending</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            )}
          </div>

          {/* Tab Navigation */}
          <div className="distributor-tabs">
            <div
              className={`distributor-tab-item ${activeTab === "commissions" ? "active" : ""}`}
              onClick={() => setActiveTab("commissions")}
            >
              Referred Subscriptions & Commissions ({commTotal})
            </div>
            <div
              className={`distributor-tab-item ${activeTab === "payouts" ? "active" : ""}`}
              onClick={() => setActiveTab("payouts")}
            >
              Payouts & Settlements History ({payouts.length})
            </div>
          </div>
        </div>

        <div className="panel-body p-4 pt-2">
          {activeTab === "commissions" ? (
            <div className="table-responsive">
              <table className="table table-hover border-0 mb-0 align-middle">
                <thead>
                  <tr className="bg-light bg-opacity-50">
                    <th className="border-0 rounded-start text-muted small fw-bold text-uppercase py-3 ps-3">Date</th>
                    <th className="border-0 text-muted small fw-bold text-uppercase py-3">Client Name</th>
                    <th className="border-0 text-muted small fw-bold text-uppercase py-3">Contact</th>
                    <th className="border-0 text-muted small fw-bold text-uppercase py-3">Service / Plan</th>
                    <th className="border-0 text-end text-muted small fw-bold text-uppercase py-3">Order Amount</th>
                    <th className="border-0 text-end text-muted small fw-bold text-uppercase py-3">Commission (₹)</th>
                    <th className="border-0 text-center text-muted small fw-bold text-uppercase py-3">Status</th>
                    <th className="border-0 rounded-end text-muted small fw-bold text-uppercase py-3 pe-3">Payout Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-5 text-muted">
                        No commissions recorded matching the criteria.
                      </td>
                    </tr>
                  ) : (
                    commissions.map((item) => (
                      <tr key={item.id} className="border-bottom border-light">
                        <td className="ps-3 py-3 text-muted small">{item.createdOn}</td>
                        <td className="py-3 fw-semibold text-dark">{item.subscriberName}</td>
                        <td className="py-3 text-muted small">
                          {item.subscriberMobile !== "—" ? item.subscriberMobile : item.subscriberEmail}
                        </td>
                        <td className="py-3">
                          <span className="badge bg-light text-dark border">{item.serviceName}</span>
                        </td>
                        <td className="text-end py-3 fw-bold text-dark">
                          ₹{Number(item.subscriptionAmount).toLocaleString()}
                        </td>
                        <td className="text-end py-3 fw-bold text-success">
                          ₹{Number(item.commissionAmount).toLocaleString()}
                          <span className="text-muted text-xs ms-1 fw-normal" style={{ fontSize: "0.7rem" }}>
                            ({item.commissionPercentage}%)
                          </span>
                        </td>
                        <td className="text-center py-3">
                          <span
                            className={`badge-status-${(item.status || "pending").toLowerCase()}`}
                          >
                            {item.status}
                          </span>
                        </td>
                        <td className="pe-3 py-3 text-muted small">
                          {item.payoutRef ? (
                            <span className="text-dark fw-medium">{item.payoutRef}</span>
                          ) : (
                            <span className="text-muted opacity-50">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {commTotal > commLimit && (
                <div className="table-pagination-container">
                  <span className="text-muted small">
                    Showing {(commPage - 1) * commLimit + 1} to{" "}
                    {Math.min(commPage * commLimit, commTotal)} of {commTotal} records
                  </span>
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={commPage <= 1}
                      onClick={() => setCommPage((p) => p - 1)}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={commPage * commLimit >= commTotal}
                      onClick={() => setCommPage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Payouts Tab */
            <div className="table-responsive">
              <table className="table table-hover border-0 mb-0 align-middle">
                <thead>
                  <tr className="bg-light bg-opacity-50">
                    <th className="border-0 rounded-start text-muted small fw-bold text-uppercase py-3 ps-3">Payout Ref</th>
                    <th className="border-0 text-muted small fw-bold text-uppercase py-3">Settlement Date</th>
                    <th className="border-0 text-end text-muted small fw-bold text-uppercase py-3">Gross Subscribed</th>
                    <th className="border-0 text-end text-muted small fw-bold text-uppercase py-3">Settled Commission</th>
                    <th className="border-0 rounded-end text-center text-muted small fw-bold text-uppercase py-3 pe-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-5 text-muted">
                        No settled payouts recorded yet.
                      </td>
                    </tr>
                  ) : (
                    payouts.map((po) => (
                      <tr key={po.id} className="border-bottom border-light">
                        <td className="ps-3 py-3 fw-bold text-dark">{po.payoutRef}</td>
                        <td className="py-3 text-muted small">{po.payoutDate}</td>
                        <td className="text-end py-3 text-dark">
                          ₹{Number(po.subscriptionAmount).toLocaleString()}
                        </td>
                        <td className="text-end py-3 fw-bold text-success fs-6">
                          ₹{Number(po.amount).toLocaleString()}
                        </td>
                        <td className="pe-3 py-3 text-center">
                          <span className="badge-status-paid">
                            <CheckCircle size={12} className="d-inline me-1" />
                            Settled & Paid
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DistributorDashboard;
